import { Box, BoxProps, Collapse } from '@mui/material';
import React, { useId, useState } from 'react';
import CollapseIcon, { CollapseIconProps } from './CollapseIcon';

interface CollapsibleRenderProps {
  icon: JSX.Element;
  getCollapse: (children: React.ReactNode, boxProps?: BoxProps) => JSX.Element;
  buttonProps: {
    id: string;
    'aria-controls': string;
    'aria-expanded': boolean;
    type: 'button' | 'submit' | 'reset';
    onClick: () => void;
  };
  isExpanded: boolean;
}

/**
 * A "headless" controller for collapisble components. We ended up with so many
 * slightly different variations of collapsible components
 * (`<CollapsibleSection>`, `<CollapsibleCard>`, `<CollapsibleBlock>`) because
 * each is styled slightly differently.
 *
 * This component is a “headless” controller that can be used to control the
 * behavior, and importantly the a11y attributes, of a collapsible component. It
 * has no styling our layout opinions, and instead uses a render prop pattern to
 * allow the consumer to fully control that, while still taking advantage of the
 * collape logic and a11y.
 *
 * For a11y guidelines:
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/
 */
export function Collapsible({
  initiallyExpanded,
  isExpanded: propIsExpanded,
  onExpanded,
  iconProps,
  children,
  buttonType = 'button',
}: {
  initiallyExpanded?: boolean;
  isExpanded?: boolean;
  onExpanded?: (expanded: boolean) => void;
  iconProps?: CollapseIconProps;
  children: (props: CollapsibleRenderProps) => JSX.Element;
  buttonType?: 'button' | 'submit' | 'reset';
}) {
  const [stateIsExpanded, setStateIsExpanded] = useState(
    propIsExpanded ?? initiallyExpanded ?? true
  );
  const buttonId = useId();
  const contentId = useId();
  const isExpanded = propIsExpanded ?? stateIsExpanded;

  const getCollapse = (children: React.ReactNode, boxProps?: BoxProps) => (
    <Collapse in={isExpanded}>
      <Box
        {...boxProps}
        id={contentId}
        role="region"
        aria-labelledby={buttonId}
      >
        {children}
      </Box>
    </Collapse>
  );

  return children({
    icon: <CollapseIcon {...iconProps} />,
    getCollapse,
    buttonProps: {
      id: buttonId,
      'aria-controls': contentId,
      'aria-expanded': isExpanded,
      type: buttonType,
      onClick: () => {
        const newExpanded = !isExpanded;
        setStateIsExpanded(newExpanded);
        onExpanded?.(newExpanded);
      },
    },
    isExpanded,
  });
}
